import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, track } from "../lib/api";
import { attribution } from "../lib/catalog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogTitle } from "./ui/dialog";
import { toast } from "sonner";
import { Search, Loader2 } from "lucide-react";

const STEPS = [
  "Fetching your website…",
  "Reading robots.txt & crawler directives…",
  "Checking for llms.txt agent index…",
  "Parsing Schema.org JSON-LD…",
  "Evaluating pricing & policy clarity…",
  "Testing agent transaction paths…",
  "Scoring payment readiness…",
  "Compiling your AI Revenue Readiness Score™…",
];

export default function ScannerWidget({ variant = "hero", buttonLabel = "Scan My Website Free" }) {
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState(0);
  const timers = useRef([]);

  const stop = () => { timers.current.forEach(clearInterval); timers.current = []; };

  const beginScan = async (e) => {
    e?.preventDefault?.();
    if (!url.trim()) { toast.error("Please enter your website URL"); return; }
    track("scanner.url_entered", { url });
    setScanning(true);
    setProgress(4);
    try {
      const attr = attribution();
      const { data } = await api.post("/scan", { url, utm: attr.utm || {}, referrer: attr.referrer || null });
      const id = data.scan_id;
      timers.current.push(setInterval(() => setProgress((p) => Math.min(94, p + (p < 60 ? 3 : 0.8))), 250));
      timers.current.push(setInterval(async () => {
        try {
          const { data: s } = await api.get(`/scan/${id}`);
          if (s.status === "completed") {
            stop(); setProgress(100);
            localStorage.setItem("ar_last_scan", id);
            track("results.viewed", { scan_id: id });
            navigate(`/results/${id}`);
          }
          if (s.status === "failed") { stop(); setScanning(false); toast.error("Scan failed — try another URL."); }
        } catch {}
      }, 2000));
    } catch {
      toast.error("Could not start the scan. Check the URL and try again.");
      setScanning(false);
    }
  };

  const isDark = variant === "hero" || variant === "dark";
  const stepIdx = Math.min(STEPS.length - 1, Math.floor((progress / 100) * STEPS.length));

  return (
    <>
      <form onSubmit={beginScan} className={`flex flex-col sm:flex-row gap-3 ${variant === "hero" ? "max-w-xl" : "max-w-lg"}`}>
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input data-testid="scan-url-input" value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Enter your website"
            className={`pl-11 h-14 text-base rounded-full ${isDark ? "bg-white/95 border-white/20 text-slate-900" : "bg-white border-slate-200"}`} />
        </div>
        <Button type="submit" data-testid="start-scan-btn" className="h-14 px-7 rounded-full bg-orange-500 hover:bg-orange-600 text-white font-semibold text-base whitespace-nowrap">
          {buttonLabel}
        </Button>
      </form>
      <p className={`text-xs mt-3 ${isDark ? "text-slate-300" : "text-slate-500"}`} data-testid="scan-microcopy">
        No credit card, no email needed to see your score. Get your AI Revenue Readiness Score™.
      </p>

      <Dialog open={scanning} onOpenChange={() => {}}>
        <DialogContent className="max-w-lg" data-testid="scan-dialog">
          <DialogTitle className="sr-only">Scanning your website</DialogTitle>
          <div className="py-4" data-testid="scan-progress">
            <div className="flex items-center gap-3 mb-6">
              <Loader2 className="h-6 w-6 text-indigo-600 animate-spin" />
              <div>
                <p className="font-heading font-semibold text-slate-900">Scanning {url.replace(/^https?:\/\//, "")}</p>
                <p className="text-sm text-slate-500">Seeing what AI sees… your score appears instantly when done.</p>
              </div>
            </div>
            <div className="h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-gradient-to-r from-indigo-600 to-violet-600 transition-all duration-200" style={{ width: `${progress}%` }} />
            </div>
            <p className="mt-4 font-mono text-sm text-indigo-600" data-testid="scan-step">{STEPS[stepIdx]}</p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
